import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera, Mic2, Lightbulb, Film, Package } from "lucide-react";
import ShareStrip from "@/components/common/ShareStrip";

/**
 * À la carte gear price sheet for Blank Canvas ($90/hr) renters.
 * Prices are scaffolded defaults — edit values in GEAR_GROUPS below.
 * All prices are PER SESSION/DAY unless suffixed with "/hr".
 */
const GEAR_GROUPS = [
  {
    key: "cameras",
    label: "Cameras & Capture",
    icon: Camera,
    color: "#00f0ff",
    items: [
      { name: "Sony 4K cinema camera (per body)", price: "$150" },
      { name: "Multi-cam 3-camera setup", price: "$350" },
      { name: "1080p multi-cam LIVE switcher", price: "$250" },
      { name: "Teleprompter rig", price: "$75" },
    ],
  },
  {
    key: "audio",
    label: "Audio",
    icon: Mic2,
    color: "#ff0055",
    items: [
      { name: "RODE PodMic (per mic, includes stand)", price: "$30" },
      { name: "Zoom L6 32-bit Float recorder", price: "$60" },
      { name: "Wireless lav kit (pair)", price: "$45" },
      { name: "Engineer-monitored sound mix", price: "$120" },
    ],
  },
  {
    key: "lighting",
    label: "Lighting",
    icon: Lightbulb,
    color: "#ffb800",
    items: [
      { name: "Tunable RGB key light", price: "$40" },
      { name: "RGB flood — wall color splash (matches your brand)", price: "$80" },
      { name: "Backdrop wash (multi-color LED)", price: "$60" },
      { name: "Full studio lighting package", price: "$180" },
      { name: "Practical / accent fixtures", price: "$25" },
    ],
  },
  {
    key: "post",
    label: "Post-Production · Our Specialty",
    icon: Film,
    color: "#a395c3",
    items: [
      { name: "Podcast episode edit (long-form, multi-cam)", price: "$350" },
      { name: "Interview / sit-down edit", price: "$300" },
      { name: "Brand content cut (commercial / promo)", price: "$450" },
      { name: "Social cuts pack (3× Reels · Shorts · TikTok)", price: "$250" },
      { name: "Branded color grade pass", price: "$180" },
      { name: "On-set producer / director", price: "$120/hr" },
    ],
  },
  {
    key: "creator",
    label: "Creator Add-Ons",
    icon: Package,
    color: "#ffb800",
    items: [
      { name: "Extra short-form reel (30 sec, edited)", price: "$75" },
      { name: "Headshot session (5 pro shots, retouched)", price: "$200" },
      { name: "Rush delivery — guaranteed 48 hr turnaround", price: "+$150" },
    ],
  },
];

const AlaCarteModal = ({ open, onClose }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/80 backdrop-blur-sm p-4 md:p-8"
          onClick={onClose}
          data-testid="alacarte-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl my-8 md:my-12 rounded-3xl border border-white/10 bg-[#0a0612] overflow-hidden"
            data-testid="alacarte-modal"
          >
            {/* Glow accents */}
            <div
              className="absolute -top-32 -right-32 w-80 h-80 rounded-full blur-3xl opacity-30 pointer-events-none"
              style={{ background: "#ff0055" }}
            />
            <div
              className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full blur-3xl opacity-25 pointer-events-none"
              style={{ background: "#00f0ff" }}
            />

            {/* Header */}
            <div className="relative px-6 md:px-10 pt-8 pb-6 border-b border-white/10">
              <ShareStrip campaign="alacarte" title="Build your own package — SB Media Studio" text="À la carte podcast + video services. Build the exact package you need." href="/#packages" className="mr-1" />
              <button
                type="button"
                onClick={onClose}
                data-testid="alacarte-close"
                className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" strokeWidth={1.8} />
              </button>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#ff0055] mb-3">
                [ Blank Canvas · Add-On Gear ]
              </div>
              <h2 className="font-display font-black tracking-tight text-2xl md:text-4xl leading-tight">
                À la carte gear pricing.
              </h2>
              <p className="mt-3 text-[#a395c3] text-sm md:text-base max-w-2xl leading-relaxed">
                Rent the room raw at <span className="text-white font-semibold">$90/hr</span> and
                bolt on only the gear you actually need. All add-ons billed per session unless
                marked <span className="text-white">/hr</span>. Prices subject to change — confirmed
                at booking.
              </p>
            </div>

            {/* Gear groups */}
            <div className="relative px-6 md:px-10 py-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              {GEAR_GROUPS.map((group) => {
                const Icon = group.icon;
                return (
                  <div
                    key={group.key}
                    data-testid={`alacarte-group-${group.key}`}
                    className="rounded-2xl bg-white/[0.03] border border-white/10 p-5 hover:border-white/20 transition-colors"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center"
                        style={{ background: `${group.color}22`, color: group.color }}
                      >
                        <Icon className="w-4 h-4" strokeWidth={1.8} />
                      </div>
                      <div
                        className="text-[10px] uppercase tracking-[0.28em] font-semibold"
                        style={{ color: group.color }}
                      >
                        {group.label}
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {group.items.map((item) => (
                        <li
                          key={item.name}
                          className="flex items-start justify-between gap-3 text-sm"
                        >
                          <span className="text-white/85 leading-snug">{item.name}</span>
                          <span className="text-white font-semibold whitespace-nowrap">
                            {item.price}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="relative px-6 md:px-10 py-6 border-t border-white/10 bg-white/[0.02] flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3 text-xs md:text-sm text-white/70">
                <Package className="w-4 h-4 text-[#00f0ff]" strokeWidth={1.8} />
                Bundle 3+ add-ons and save 10% — applied at checkout.
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href="mailto:Info@sbmediastudio.com?subject=Blank%20Canvas%20%C3%A0%20la%20carte%20question"
                  data-testid="alacarte-email-link"
                  className="text-sm text-white/70 hover:text-[#00f0ff] transition-colors"
                >
                  Have a question? Email us →
                </a>
                
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    window.dispatchEvent(
                      new CustomEvent("sb:open-booking", {
                        detail: { package: "custom" },
                      }),
                    );
                  }}
                  data-testid="alacarte-book-cta"
                  className="inline-flex items-center gap-2 pl-4 pr-2 py-2 rounded-full bg-[#ff0055] text-white font-semibold text-sm hover:bg-white hover:text-[#05050a] transition-colors cursor-pointer touch-manipulation"
                >
                  Book the Blank Canvas
                  <span className="w-6 h-6 rounded-full bg-black/30 flex items-center justify-center text-[10px]">
                    →
                  </span>
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AlaCarteModal;
